import React, { useMemo } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Film,
  Image,
  CheckSquare,
  Square,
  MinusSquare,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  AdminContentItemDto,
  ContentStatus,
  ContentType,
  ContentSource,
  ContentOwner,
} from '../../types/content';
import {
  cn,
  formatRelativeTime,
  getContentStatusLabel,
  getContentStatusBadgeVariant,
  getContentSourceLabel,
  getContentTypeLabel,
  formatVideoDuration,
} from '../../utils/helpers';

// ============================================================================
// Types
// ============================================================================

export interface ContentFilters {
  status?: ContentStatus | 'ALL';
  type?: ContentType;
  source?: ContentSource;
  search?: string;
}

interface ContentListProps {
  items: AdminContentItemDto[];
  loading: boolean;
  selectedId: string | null;
  selectedIds: Set<string>;
  filters: ContentFilters;
  total: number;
  page: number;
  totalPages: number;
  ownerTotalCounts?: Map<number, number>;
  onSelectItem: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onFilterChange: (filters: ContentFilters) => void;
  onPageChange: (page: number) => void;
}

// ============================================================================
// Status Tabs
// ============================================================================

const statusTabs: { label: string; value: ContentStatus | 'ALL' }[] = [
  { label: 'Pending', value: ContentStatus.PENDING_REVIEW },
  { label: 'Pending Import', value: ContentStatus.PENDING_IMPORT },
  { label: 'Ready', value: ContentStatus.READY },
  { label: 'Rejected', value: ContentStatus.REJECTED },
  { label: 'All', value: 'ALL' },
];

// ============================================================================
// Badge variant classes
// ============================================================================

const badgeClasses: Record<string, string> = {
  default: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  warning: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  destructive: 'bg-red-500/15 text-red-400 border-red-500/30',
  secondary: 'bg-muted/50 text-muted-foreground border-border',
};

// ============================================================================
// Grouped owner type
// ============================================================================

interface OwnerGroup {
  owner: ContentOwner;
  items: AdminContentItemDto[];
}

// ============================================================================
// Component
// ============================================================================

const ContentList: React.FC<ContentListProps> = ({
  items,
  loading,
  selectedId,
  selectedIds,
  filters,
  total,
  page,
  totalPages,
  ownerTotalCounts,
  onSelectItem,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onFilterChange,
  onPageChange,
}) => {
  const allSelected = useMemo(
    () => items.length > 0 && items.every((item) => selectedIds.has(item.id)),
    [items, selectedIds]
  );
  const someSelected = useMemo(
    () => items.some((item) => selectedIds.has(item.id)) && !allSelected,
    [items, selectedIds, allSelected]
  );

  // Group items by owner
  const ownerGroups: OwnerGroup[] = useMemo(() => {
    const map = new Map<number, OwnerGroup>();
    for (const item of items) {
      const existing = map.get(item.owner.id);
      if (existing) {
        existing.items.push(item);
      } else {
        map.set(item.owner.id, { owner: item.owner, items: [item] });
      }
    }
    return Array.from(map.values());
  }, [items]);

  // Collapsed state managed with a set of owner ids
  const [collapsedOwners, setCollapsedOwners] = React.useState<Set<number>>(new Set());

  const toggleOwner = (ownerId: number) => {
    setCollapsedOwners((prev) => {
      const next = new Set(prev);
      if (next.has(ownerId)) {
        next.delete(ownerId);
      } else {
        next.add(ownerId);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-border flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by caption or owner..."
            value={filters.search || ''}
            onChange={(e) =>
              onFilterChange({ ...filters, search: e.target.value || undefined })
            }
            className={cn(
              'w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background',
              'text-sm text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            )}
          />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-border flex-shrink-0 overflow-x-auto">
        {statusTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => onFilterChange({ ...filters, status: tab.value })}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              filters.status === tab.value
                ? 'bg-primary/15 text-primary border border-primary/30'
                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Type/Source Filters */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border flex-shrink-0">
        <select
          value={filters.type || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              type: (e.target.value as ContentType) || undefined,
            })
          }
          className={cn(
            'flex-1 px-2 py-1.5 rounded-lg border border-border bg-background',
            'text-xs text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-primary/50'
          )}
        >
          <option value="">All Types</option>
          {Object.values(ContentType).map((t) => (
            <option key={t} value={t}>
              {getContentTypeLabel(t)}
            </option>
          ))}
        </select>

        <select
          value={filters.source || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              source: (e.target.value as ContentSource) || undefined,
            })
          }
          className={cn(
            'flex-1 px-2 py-1.5 rounded-lg border border-border bg-background',
            'text-xs text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-primary/50'
          )}
        >
          <option value="">All Sources</option>
          {Object.values(ContentSource).map((s) => (
            <option key={s} value={s}>
              {getContentSourceLabel(s)}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk Select Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => (allSelected || someSelected ? onDeselectAll() : onSelectAll())}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {allSelected ? (
              <CheckSquare className="h-4 w-4 text-primary" />
            ) : someSelected ? (
              <MinusSquare className="h-4 w-4 text-primary" />
            ) : (
              <Square className="h-4 w-4" />
            )}
          </button>
          <span className="text-xs text-muted-foreground">
            {selectedIds.size > 0
              ? `${selectedIds.size} selected`
              : `${total} item${total !== 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Content Items - Grouped by Owner */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-lg animate-pulse">
                <div className="w-16 h-16 bg-muted/30 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-muted/30 rounded" />
                  <div className="h-3 w-1/2 bg-muted/30 rounded" />
                  <div className="h-3 w-1/3 bg-muted/30 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Film className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No content found</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Try adjusting your filters
            </p>
          </div>
        ) : (
          <div>
            {ownerGroups.map((group) => {
              const isCollapsed = collapsedOwners.has(group.owner.id);

              return (
                <div key={group.owner.id}>
                  {/* Owner Header */}
                  <button
                    onClick={() => toggleOwner(group.owner.id)}
                    className={cn(
                      'w-full flex items-center gap-2.5 px-3 py-2.5',
                      'bg-muted/20 border-b border-border',
                      'hover:bg-muted/30 transition-colors sticky top-0 z-10'
                    )}
                  >
                    {group.owner.profileImage ? (
                      <img
                        src={group.owner.profileImage}
                        alt={group.owner.name}
                        className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <User className="h-3.5 w-3.5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {group.owner.name}
                      </p>
                    </div>
                    <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-full">
                      {ownerTotalCounts?.get(group.owner.id) ?? group.items.length}
                    </span>
                    {isCollapsed ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronUp className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    )}
                  </button>

                  {/* Owner's Content Items */}
                  {!isCollapsed && (
                    <div className="divide-y divide-border/50">
                      {group.items.map((item) => {
                        const isSelected = selectedId === item.id;
                        const isChecked = selectedIds.has(item.id);
                        const badgeVariant = getContentStatusBadgeVariant(item.status);

                        return (
                          <div
                            key={item.id}
                            className={cn(
                              'flex items-start gap-3 p-3 pl-6 cursor-pointer transition-colors',
                              isSelected
                                ? 'bg-primary/10 border-l-2 border-l-primary'
                                : 'hover:bg-muted/30 border-l-2 border-l-transparent'
                            )}
                          >
                            {/* Checkbox */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleSelect(item.id);
                              }}
                              className="flex-shrink-0 mt-1 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {isChecked ? (
                                <CheckSquare className="h-4 w-4 text-primary" />
                              ) : (
                                <Square className="h-4 w-4" />
                              )}
                            </button>

                            {/* Thumbnail */}
                            <div
                              className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-muted/20 relative"
                              onClick={() => onSelectItem(item.id)}
                            >
                              {item.posterUrl ? (
                                <img
                                  src={item.posterUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  {item.type === ContentType.VIDEO ? (
                                    <Film className="h-5 w-5 text-muted-foreground/50" />
                                  ) : (
                                    <Image className="h-5 w-5 text-muted-foreground/50" />
                                  )}
                                </div>
                              )}
                              {item.type === ContentType.VIDEO && item.durationSec && (
                                <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[10px] px-1 rounded">
                                  {formatVideoDuration(item.durationSec)}
                                </span>
                              )}
                            </div>

                            {/* Info */}
                            <div
                              className="flex-1 min-w-0"
                              onClick={() => onSelectItem(item.id)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={cn(
                                    'text-[10px] font-medium px-1.5 py-0.5 rounded border',
                                    badgeClasses[badgeVariant]
                                  )}
                                >
                                  {getContentStatusLabel(item.status)}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {getContentSourceLabel(item.source)}
                                </span>
                              </div>
                              <p className="text-sm text-foreground truncate">
                                {item.caption || 'No caption'}
                              </p>
                              <span className="text-xs text-muted-foreground/50">
                                {formatRelativeTime(item.createdAt)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border flex-shrink-0">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              page <= 1
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              page >= totalPages
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ContentList;
